/**
 * ClientCollectionView — kanban-style per-(run × client) workspace.
 *
 * Three columns:
 *   Red    "חסרים"     — docs the client owes but hasn't uploaded yet
 *   Yellow "ממתינים לבדיקה" — uploaded, dashboard-decrypted, awaiting accountant review
 *   Green  "אושרו"      — accountant clicked Approve
 *
 * Rejected files surface inside the "Missing" column (they're functionally
 * missing again — the client needs to re-upload). Each rejected card
 * shows the rejection reason inline.
 *
 * Right side panel: DocumentToolkitPanel — accountant's saved templates +
 * free-text input to add extra required docs to THIS client × THIS run.
 *
 * Route: /runs/:runId/clients/:clientId
 */

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError, api, type FileStatus } from "../lib/api";

interface CollectionFile {
  file_id: string;
  document_label: string | null;
  size_bytes: number;
  status: FileStatus;
  uploaded_at: string;
  rejection_reason: string | null;
  approved_at: string | null;
}

interface Collection {
  run_id: string;
  client_id: string;
  client_name: string;
  client_phone: string | null;
  reminders_paused: boolean;
  expected_payment_minor: number | null;
  reminder_schedule_days: number[] | null;
  invite_id: string | null;
  required_docs: string[];
  base_required_docs: string[];
  extra_required_docs: string[];
  files: CollectionFile[];
}

export function ClientCollectionView() {
  const { runId, clientId } = useParams<{ runId: string; clientId: string }>();
  const [data, setData] = useState<Collection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<CollectionFile | null>(null);
  // file_id currently mid-approve/reject — disables that card's buttons
  // so a double-click can't fire two audited mutations.
  const [busyFileId, setBusyFileId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!runId || !clientId) return;
    try {
      setError(null);
      const resp = await api.runClientCollection(runId, clientId);
      setData(resp);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "fetch_failed");
    }
  }, [runId, clientId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!runId || !clientId) return <Text>חסר מזהה</Text>;
  if (error) return <Alert color="red">שגיאה: {error}</Alert>;
  if (data === null) return <Loader />;

  // Bucket files by review state, then pair with required_docs to compute "missing"
  const filesByLabel = new Map<string, CollectionFile[]>();
  for (const f of data.files) {
    const key = f.document_label ?? "(ללא תווית)";
    const arr = filesByLabel.get(key) ?? [];
    arr.push(f);
    filesByLabel.set(key, arr);
  }

  // For each required doc, decide where it lives.
  const missingDocs: string[] = [];
  const rejectedFiles: CollectionFile[] = [];
  const pendingReviewFiles: CollectionFile[] = [];
  const approvedFiles: CollectionFile[] = [];
  const archivedFiles: CollectionFile[] = [];

  for (const doc of data.required_docs) {
    const matches = filesByLabel.get(doc) ?? [];
    const active = matches.filter((f) => f.status !== "archived");
    if (active.length === 0) {
      missingDocs.push(doc);
      continue;
    }
    // Use the newest file as the "current state" for this doc.
    const newest = active[0];
    if (newest.status === "rejected") {
      rejectedFiles.push(newest);
    } else if (newest.status === "approved") {
      approvedFiles.push(newest);
    } else {
      pendingReviewFiles.push(newest);
    }
  }
  // Files without a required-doc label (free uploads) still surface in
  // their natural column rather than vanish.
  for (const f of data.files) {
    if (f.document_label && data.required_docs.includes(f.document_label)) continue;
    if (f.status === "archived") archivedFiles.push(f);
    else if (f.status === "rejected") rejectedFiles.push(f);
    else if (f.status === "approved") approvedFiles.push(f);
    else pendingReviewFiles.push(f);
  }

  const doApprove = async (fileId: string) => {
    if (busyFileId) return; // a mutation is already in flight
    setBusyFileId(fileId);
    try {
      await api.approveFile(fileId);
      await refresh();
    } finally {
      setBusyFileId(null);
    }
  };

  const onRejectSubmit = async (reason: string) => {
    if (!rejectTarget) return;
    setBusyFileId(rejectTarget.file_id);
    try {
      await api.rejectFile(rejectTarget.file_id, reason);
      setRejectTarget(null);
      await refresh();
    } finally {
      setBusyFileId(null);
    }
  };

  const totalRequired = data.required_docs.length;
  const progressPct =
    totalRequired === 0
      ? 100
      : Math.round((approvedFiles.length / totalRequired) * 100);

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Anchor component={Link} to={`/runs/${runId}`} size="sm">
            ← חזרה למטריצת הקמפיין
          </Anchor>
          <Title order={1}>{data.client_name}</Title>
          <Text c="dimmed" size="sm">
            {data.client_phone ?? "—"} ·{" "}
            {approvedFiles.length}/{totalRequired} מסמכים אושרו ({progressPct}%)
            {data.reminders_paused && (
              <Badge color="orange" ml="xs">
                תזכורות מושהות
              </Badge>
            )}
          </Text>
        </div>
      </Group>

      <ClientEditorsRow data={data} onChanged={refresh} />

      <Group align="flex-start" gap="md" grow={false} wrap="nowrap">
        <Stack style={{ flex: 1 }} gap="md">
          <KanbanColumn
            title="חסרים"
            color="red"
            count={missingDocs.length + rejectedFiles.length}
          >
            {missingDocs.map((doc) => (
              <MissingCard key={`m-${doc}`} label={doc} />
            ))}
            {rejectedFiles.map((f) => (
              <RejectedCard key={f.file_id} file={f} />
            ))}
          </KanbanColumn>

          <KanbanColumn
            title="ממתינים לבדיקה"
            color="yellow"
            count={pendingReviewFiles.length}
          >
            {pendingReviewFiles.map((f) => (
              <PendingReviewCard
                key={f.file_id}
                file={f}
                busy={busyFileId === f.file_id}
                onApprove={() => doApprove(f.file_id)}
                onReject={() => setRejectTarget(f)}
              />
            ))}
          </KanbanColumn>

          <KanbanColumn
            title="אושרו"
            color="green"
            count={approvedFiles.length}
          >
            {approvedFiles.map((f) => (
              <ApprovedCard key={f.file_id} file={f} />
            ))}
          </KanbanColumn>
        </Stack>

        <div style={{ width: 320, flexShrink: 0 }}>
          <DocumentToolkitPanel data={data} onChanged={refresh} />
        </div>
      </Group>

      <RejectReasonModal
        open={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        file={rejectTarget}
        onSubmit={onRejectSubmit}
      />
    </Stack>
  );
}

// ───────────────────────────── kanban primitives

function KanbanColumn({
  title,
  color,
  count,
  children,
}: {
  title: string;
  color: "red" | "yellow" | "green";
  count: number;
  children: React.ReactNode;
}) {
  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" mb="xs">
        <Title order={4}>{title}</Title>
        <Badge color={color} variant="light">
          {count}
        </Badge>
      </Group>
      <Stack gap="xs">{children}</Stack>
      {count === 0 && (
        <Text c="dimmed" size="sm">
          (ריק)
        </Text>
      )}
    </Paper>
  );
}

function MissingCard({ label }: { label: string }) {
  return (
    <Card withBorder padding="xs" style={{ borderColor: "#fa5252" }}>
      <Text fw={500}>{label}</Text>
      <Text c="dimmed" size="xs">
        לקוח טרם העלה
      </Text>
    </Card>
  );
}

function RejectedCard({ file }: { file: CollectionFile }) {
  return (
    <Card withBorder padding="xs" style={{ borderColor: "#fa5252" }}>
      <Text fw={500}>{file.document_label ?? "(ללא תווית)"}</Text>
      <Text c="red" size="xs">
        נדחה — נשלח ללקוח להעלאה מחדש
      </Text>
      {file.rejection_reason && (
        <Text size="xs" mt={4} c="dimmed">
          סיבה: {file.rejection_reason}
        </Text>
      )}
    </Card>
  );
}

function PendingReviewCard({
  file,
  busy,
  onApprove,
  onReject,
}: {
  file: CollectionFile;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <Card withBorder padding="xs" style={{ borderColor: "#fab005" }}>
      <Text fw={500}>{file.document_label ?? "(ללא תווית)"}</Text>
      <Text c="dimmed" size="xs">
        הועלה: {new Date(file.uploaded_at).toLocaleString("he-IL")} ·{" "}
        {(file.size_bytes / 1024).toFixed(1)} KB
      </Text>
      <Group gap={6} mt="xs">
        <Button
          size="xs"
          color="green"
          variant="light"
          loading={busy}
          disabled={busy}
          onClick={onApprove}
        >
          אשר
        </Button>
        <Button
          size="xs"
          color="red"
          variant="light"
          disabled={busy}
          onClick={onReject}
        >
          דחה
        </Button>
      </Group>
    </Card>
  );
}

function ApprovedCard({ file }: { file: CollectionFile }) {
  return (
    <Card withBorder padding="xs" style={{ borderColor: "#40c057" }}>
      <Text fw={500}>{file.document_label ?? "(ללא תווית)"}</Text>
      <Text c="dimmed" size="xs">
        אושר{" "}
        {file.approved_at
          ? new Date(file.approved_at).toLocaleString("he-IL")
          : ""}
      </Text>
    </Card>
  );
}

// ───────────────────────────── reject reason modal

function RejectReasonModal({
  open,
  onClose,
  file,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  file: CollectionFile | null;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await onSubmit(reason.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={open} onClose={onClose} title="דחיית קובץ" size="md">
      <Stack>
        <Text size="sm" c="dimmed">
          הסיבה תוצג ללקוח בעמוד ההעלאה כדי שידע מה לתקן.
        </Text>
        {file && (
          <Text size="sm">
            <strong>קובץ:</strong> {file.document_label ?? "(ללא תווית)"}
          </Text>
        )}
        <Textarea
          label="סיבה"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={3}
          placeholder='לדוגמה: "המסמך לשנת 2024 — נא להעלות לשנת 2025"'
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            ביטול
          </Button>
          <Button
            color="red"
            loading={busy}
            disabled={!reason.trim()}
            onClick={submit}
          >
            שלח דחייה
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ───────────────────────────── client editors row (payment + schedule)

function ClientEditorsRow({
  data,
  onChanged,
}: {
  data: Collection;
  onChanged: () => Promise<void>;
}) {
  const [payment, setPayment] = useState<string | number>(
    data.expected_payment_minor !== null
      ? data.expected_payment_minor / 100
      : "",
  );
  const [scheduleTags, setScheduleTags] = useState<string[]>(
    (data.reminder_schedule_days ?? []).map(String),
  );
  const [busyPayment, setBusyPayment] = useState(false);
  const [busySchedule, setBusySchedule] = useState(false);

  const savePayment = async () => {
    setBusyPayment(true);
    try {
      const minor =
        payment === "" || payment === null
          ? null
          : Math.round(Number(payment) * 100);
      await api.clientSetPayment(data.client_id, minor);
      await onChanged();
    } finally {
      setBusyPayment(false);
    }
  };

  const saveSchedule = async () => {
    setBusySchedule(true);
    try {
      const days = scheduleTags
        .map((s) => Number(s))
        .filter((n) => Number.isInteger(n) && n >= 0 && n <= 365);
      await api.clientSetReminderSchedule(
        data.client_id,
        days.length === 0 ? null : days,
      );
      await onChanged();
    } finally {
      setBusySchedule(false);
    }
  };

  return (
    <Paper withBorder p="md">
      <Group align="flex-end" gap="md" wrap="wrap">
        <NumberInput
          label="תשלום צפוי (₪)"
          description="לידיעת המשרד בלבד; לא מועבר ללקוח"
          value={payment}
          onChange={setPayment}
          min={0}
          max={10_000_000}
          decimalScale={2}
          fixedDecimalScale={false}
          thousandSeparator=","
          allowNegative={false}
          w={200}
        />
        <Button onClick={savePayment} loading={busyPayment} variant="light">
          שמור תשלום
        </Button>
        <div style={{ flex: 1 }}>
          <TagsInput
            label="לוח תזכורות מותאם (ימים לפני דדליין)"
            description="ריק = ירושת ברירת המחדל (7, 3, 1)"
            value={scheduleTags}
            onChange={setScheduleTags}
            placeholder="הקלד מספר ולחץ Enter"
            data={[]}
          />
        </div>
        <Button onClick={saveSchedule} loading={busySchedule} variant="light">
          שמור לוח
        </Button>
      </Group>
    </Paper>
  );
}

// ───────────────────────────── floating doc toolkit

function DocumentToolkitPanel({
  data,
  onChanged,
}: {
  data: Collection;
  onChanged: () => Promise<void>;
}) {
  const [templates, setTemplates] = useState<
    { template_id: string; name: string; documents: string[] }[]
  >([]);
  const [customDoc, setCustomDoc] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .templatesList()
      .then((r) => setTemplates(r.templates))
      .catch(() => setTemplates([]));
  }, []);

  const inUse = new Set(data.extra_required_docs);

  const persistExtras = async (next: string[]) => {
    if (!data.invite_id) {
      setError("אין הזמנה ללקוח זה — צור הזמנה לפני הוספת מסמכים");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.inviteSetExtraDocs(data.invite_id, next);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "save_failed");
    } finally {
      setBusy(false);
    }
  };

  const addOne = async (label: string) => {
    const trimmed = label.trim();
    if (!trimmed || inUse.has(trimmed)) return;
    await persistExtras([...data.extra_required_docs, trimmed]);
  };

  const removeOne = async (label: string) => {
    await persistExtras(data.extra_required_docs.filter((d) => d !== label));
  };

  const addFromTemplate = async (docs: string[]) => {
    const merged = [...data.extra_required_docs];
    for (const d of docs) {
      if (!merged.includes(d) && !data.base_required_docs.includes(d)) {
        merged.push(d);
      }
    }
    await persistExtras(merged);
  };

  const addCustom = async () => {
    if (!customDoc.trim()) return;
    await addOne(customDoc);
    setCustomDoc("");
  };

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Title order={4}>תוספות לרשימת המסמכים</Title>
        <Text c="dimmed" size="xs">
          התווסף ללקוח זה בלבד, מעבר למה שהקמפיין מבקש מכלל הלקוחות.
        </Text>

        {error && <Alert color="red">{error}</Alert>}

        {data.extra_required_docs.length > 0 && (
          <Card withBorder padding="xs">
            <Text fw={500} size="sm" mb="xs">
              פעילים
            </Text>
            <Stack gap={4}>
              {data.extra_required_docs.map((d) => (
                <Group key={d} justify="space-between">
                  <Text size="sm">{d}</Text>
                  <Anchor
                    size="xs"
                    component="button"
                    onClick={() => removeOne(d)}
                  >
                    הסר
                  </Anchor>
                </Group>
              ))}
            </Stack>
          </Card>
        )}

        <Card withBorder padding="xs">
          <Text fw={500} size="sm" mb="xs">
            תבניות שמורות
          </Text>
          {templates.length === 0 ? (
            <Text c="dimmed" size="xs">
              עוד אין תבניות. צור תבנית במסך תבניות המסמכים.
            </Text>
          ) : (
            <Stack gap={4}>
              {templates.map((t) => (
                <Group key={t.template_id} justify="space-between">
                  <div>
                    <Text size="sm">{t.name}</Text>
                    <Text c="dimmed" size="xs">
                      {t.documents.length} מסמכים
                    </Text>
                  </div>
                  <Button
                    size="xs"
                    variant="light"
                    loading={busy}
                    onClick={() => addFromTemplate(t.documents)}
                  >
                    + הוסף
                  </Button>
                </Group>
              ))}
            </Stack>
          )}
        </Card>

        <Card withBorder padding="xs">
          <Text fw={500} size="sm" mb="xs">
            מסמך מותאם
          </Text>
          <Group gap="xs">
            <TextInput
              value={customDoc}
              onChange={(e) => setCustomDoc(e.currentTarget.value)}
              placeholder='לדוגמה: "תעודת רכישה"'
              style={{ flex: 1 }}
              maxLength={120}
              onKeyDown={(e) => {
                if (e.key === "Enter") void addCustom();
              }}
            />
            <Button
              size="xs"
              variant="light"
              loading={busy}
              onClick={addCustom}
              disabled={!customDoc.trim()}
            >
              +
            </Button>
          </Group>
        </Card>
      </Stack>
    </Paper>
  );
}
